import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { MarketBanner, MarketButton } from '@market/react';
import { BUNDLE_OFFER_RESOLUTIONS } from 'constants/software';
import { formatTierMonthlyPrice } from 'components/common/receipt/bundle_offer_utils';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useUpdateLineItemQuantity } from 'typescript/hooks/cart_client/use_update_line_item_quantity';
import { QUERY_KEYS } from 'typescript/constants/react_query';
import {
  BundleOfferTierKey,
  HardwareBundleOffer,
} from 'typescript/types/api/solidshop';

// Resolutions that surface a payment-step message. Other resolutions
// (plus / premium / suppress) are silent at payment — the cart's
// <BundleOfferCard> is their only surface.
const MESSAGE_RESOLUTIONS = new Set<string>([
  BUNDLE_OFFER_RESOLUTIONS.ALREADY_SUBSCRIBED,
  BUNDLE_OFFER_RESOLUTIONS.UPGRADE_TO_PREMIUM,
]);

const tierLabel = (
  offer: HardwareBundleOffer,
  tierKey: BundleOfferTierKey | null,
): string | null => (tierKey ? (offer.tiers[tierKey]?.label ?? null) : null);

/**
 * Surfaces, at the payment step, why the seller's cart looks the way it does
 * after the cart-merge reconciler ran on sign-in:
 *   - already_subscribed   — they already hold the recommended tier or higher
 *   - upgrade_to_premium   — they're on Plus but the hardware wants Premium
 *
 * Renders nothing for every other resolution (and when no payload exists, which
 * is the not-signed-in case — the resolver needs a merchant token to run).
 */
const BundleReconciliationMessage = ({
  className = '',
}: HTMLAttributes<HTMLElement>) => {
  const { t } = useTranslation();
  const { orderNumber } = useCheckoutFlow();
  const queryClient = useQueryClient();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const { updateLineItemQuantity, isPending } = useUpdateLineItemQuantity();

  const offer = orderSummaryData?.hardware_bundle_offer;
  if (!offer || !MESSAGE_RESOLUTIONS.has(offer.resolution)) {
    return null;
  }

  if (offer.resolution === BUNDLE_OFFER_RESOLUTIONS.ALREADY_SUBSCRIBED) {
    const label = tierLabel(offer, offer.active_tier);
    return (
      <MarketBanner className={className} variant="success">
        <p data-testid="bundle-reconciliation-already-subscribed">
          {t('cart.bundleOffer.alreadySubscribed', { label })}
        </p>
      </MarketBanner>
    );
  }

  // upgrade_to_premium: seller is on Plus and the hardware works best with
  // Premium. Compute the $/mo delta FE-side from the tier view-model and offer
  // a one-tap swap of the cart bundle line to the Premium variant.
  const plusTier = offer.tiers.plus;
  const premiumTier = offer.tiers.premium;
  const activeLabel = tierLabel(offer, offer.active_tier);
  const hasDelta =
    typeof plusTier?.monthly_cents === 'number' &&
    typeof premiumTier?.monthly_cents === 'number';
  const deltaPrice = hasDelta
    ? formatTierMonthlyPrice({
        monthly_cents: premiumTier.monthly_cents - plusTier.monthly_cents,
        currency: premiumTier.currency,
      })
    : null;

  const premiumVariantToken = premiumTier?.variant_token;

  const onUpgrade = () => {
    if (!premiumVariantToken) return;
    updateLineItemQuantity(
      { variantToken: premiumVariantToken, quantity: 1, action: 'add' },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
          });
        },
      },
    );
  };

  return (
    <MarketBanner className={className} variant="info">
      <p data-testid="bundle-reconciliation-upgrade">
        {t('cart.bundleOffer.upgradeMessage', {
          label: activeLabel,
          price: deltaPrice,
        })}
      </p>
      {premiumVariantToken && (
        <MarketButton
          slot="action"
          rank="secondary"
          {...(isPending && { disabled: true })}
          onClick={onUpgrade}
        >
          {t('cart.bundleOffer.upgradeCta')}
        </MarketButton>
      )}
    </MarketBanner>
  );
};

export default BundleReconciliationMessage;
